/**
 * Image optimization utilities for CraftyJoy
 * Handles responsive images, format conversion, and lazy loading
 */

export interface ImageSizeConfig {
  width: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
}

export interface ResponsiveImageConfig {
  sizes: ImageSizeConfig[];
  breakpoints?: number[];
  fallback: ImageSizeConfig;
}

/**
 * Generate responsive image URLs for external services
 */
export class ImageOptimizer {
  private static readonly PEXELS_BASE = 'https://images.pexels.com/photos/';
  private static readonly UNSPLASH_BASE = 'https://images.unsplash.com/';

  /**
   * Optimize Pexels images with different sizes and formats
   */
  static optimizePexelsImage(
    originalUrl: string,
    config: ImageSizeConfig
  ): string {
    if (!originalUrl.includes('pexels.com')) return originalUrl;

    const { width, height, quality = 80, format = 'webp' } = config;
    
    // Extract photo ID from URL
    const match = originalUrl.match(/photos\/(\d+)\//);
    if (!match) return originalUrl;
    
    const photoId = match[1];
    const heightParam = height ? `&h=${height}` : '';
    const qualityParam = quality !== 80 ? `&q=${quality}` : '';
    
    return `${this.PEXELS_BASE}${photoId}/pexels-photo-${photoId}.${format}?auto=compress&cs=tinysrgb&w=${width}${heightParam}${qualityParam}`;
  }

  /**
   * Optimize Unsplash images
   */
  static optimizeUnsplashImage(
    originalUrl: string,
    config: ImageSizeConfig
  ): string {
    if (!originalUrl.includes('unsplash.com')) return originalUrl;

    const { width, height, quality = 80, format = 'webp' } = config;
    const url = new URL(originalUrl);
    
    url.searchParams.set('w', width.toString());
    if (height) url.searchParams.set('h', height.toString());
    url.searchParams.set('q', quality.toString());
    url.searchParams.set('fm', format);
    url.searchParams.set('fit', 'crop');
    
    return url.toString();
  }

  /**
   * Generate responsive image srcSet
   */
  static generateSrcSet(
    originalUrl: string,
    config: ResponsiveImageConfig
  ): { srcSet: string; sizes: string; src: string } {
    const { sizes, breakpoints = [640, 768, 1024, 1280], fallback } = config;
    
    const srcSetItems = sizes.map((size, index) => {
      const optimizedUrl = this.optimizeImage(originalUrl, size);
      const breakpoint = breakpoints[index];
      return breakpoint ? `${optimizedUrl} ${breakpoint}w` : `${optimizedUrl} ${size.width}w`;
    });

    const sizesAttr = breakpoints
      .map((bp, index) => index === breakpoints.length - 1 
        ? `${sizes[index].width}px` 
        : `(max-width: ${bp}px) ${sizes[index].width}px`
      )
      .join(', ');

    return {
      srcSet: srcSetItems.join(', '),
      sizes: sizesAttr,
      src: this.optimizeImage(originalUrl, fallback)
    };
  }

  /**
   * Main optimization method that routes to appropriate service
   */
  static optimizeImage(url: string, config: ImageSizeConfig): string {
    if (url.includes('pexels.com')) {
      return this.optimizePexelsImage(url, config);
    }
    if (url.includes('unsplash.com')) {
      return this.optimizeUnsplashImage(url, config);
    }
    
    // For other URLs, return as-is
    return url;
  }

  /**
   * Check if browser supports WebP
   */
  static supportsWebP(): Promise<boolean> {
    return new Promise((resolve) => {
      const webP = new Image();
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2);
      };
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  }

  /**
   * Get optimal image format based on browser support
   */
  static async getOptimalFormat(): Promise<'webp' | 'jpeg'> {
    const supportsWebP = await this.supportsWebP();
    return supportsWebP ? 'webp' : 'jpeg';
  }

  /**
   * Preload critical images
   */
  static preloadImages(urls: string[]): void {
    urls.forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    });
  }

  /**
   * Create a low-quality image placeholder for blur effect
   */
  static generateBlurPlaceholder(
    originalUrl: string,
    size: number = 10
  ): string {
    return this.optimizeImage(originalUrl, {
      width: size,
      height: size,
      quality: 20,
      format: 'jpeg'
    });
  }
}

/**
 * Common image size presets
 */
export const IMAGE_PRESETS = {
  avatar: {
    xs: { width: 24, height: 24 },
    sm: { width: 32, height: 32 },
    md: { width: 40, height: 40 },
    lg: { width: 48, height: 48 },
    xl: { width: 64, height: 64 },
    '2xl': { width: 80, height: 80 }
  },
  thumbnail: {
    small: { width: 150, height: 100 },
    medium: { width: 300, height: 200 },
    large: { width: 600, height: 400 }
  },
  hero: {
    mobile: { width: 640, height: 400 },
    tablet: { width: 1024, height: 600 },
    desktop: { width: 1920, height: 1080 }
  }
} as const;

/**
 * React hook for image optimization
 */
export const useOptimizedImage = (
  url: string,
  config: ImageSizeConfig
) => {
  const optimizedUrl = ImageOptimizer.optimizeImage(url, config);
  const blurPlaceholder = ImageOptimizer.generateBlurPlaceholder(url);
  
  return {
    src: optimizedUrl,
    blurDataURL: blurPlaceholder
  };
};